/**
 * Advanced adjunction diagrams: Beck-Chevalley squares, seam
 * bridge Kan extensions, 2-adjunctions, and graded adjunctions.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../../../design-system/tokens.js";
import {
  DIAGRAM_MONO,
  STROKE,
  LABEL,
  WARN_COLOR,
  ArrowDefs,
  ObjectNode,
  Arrow,
} from "./primitives.js";

/**
 * BeckChevalleySquareDiagram: a pullback square decorated with two
 * adjunctions and a *mate* 2-cell. The diagram has two display modes,
 * controlled by the `mode` prop:
 *
 *   - `"holds"` — the mate is an isomorphism (`≅`); the square commutes and
 *     the dotted bridge can be safely walked in either direction.
 *   - `"fails"` — the mate is *not* an isomorphism; the diagonal is stamped
 *     with `✗`, marking the seam. CSK uses this regime as a structural
 *     security guarantee: the "free" crossing is unconstructable.
 *
 *        A ──── f ────▶ B
 *        │              │
 *        h              g
 *        ▼              ▼
 *        C ──── k ────▶ D
 *
 *   Mate:  Σ_g ∘ f  →  k ∘ Σ_h    (iso ⇒ BC holds; non-iso ⇒ seam)
 */
export function BeckChevalleySquareDiagram({
  mode = "holds",
  accent = STROKE,
  topLeft = "A",
  topRight = "B",
  bottomLeft = "C",
  bottomRight = "D",
  ariaLabel,
}: {
  readonly mode?: "holds" | "fails";
  readonly accent?: string;
  readonly topLeft?: string;
  readonly topRight?: string;
  readonly bottomLeft?: string;
  readonly bottomRight?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const subtleMarkerId = `${markerId}-subtle`;
  const failColor = WARN_COLOR;
  const isHolds = mode === "holds";
  const label =
    ariaLabel ??
    (isHolds
      ? "Beck-Chevalley square: the mate Σ_g ∘ f ≅ k ∘ Σ_h is an isomorphism, so data flows freely."
      : "Beck-Chevalley failure: the mate Σ_g ∘ f → k ∘ Σ_h is not an isomorphism, so the square has a seam.");
  return (
    <svg
      viewBox="0 0 420 290"
      role="img"
      aria-label={label}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ArrowDefs id={subtleMarkerId} color={isHolds ? accent : failColor} />
      <ObjectNode cx={60} cy={60} label={topLeft} accent={accent} />
      <ObjectNode cx={360} cy={60} label={topRight} accent={accent} />
      <ObjectNode cx={60} cy={220} label={bottomLeft} accent={accent} />
      <ObjectNode cx={360} cy={220} label={bottomRight} accent={accent} />
      <Arrow x1={74} y1={60} x2={344} y2={60} label="f" markerId={markerId} color={accent} />
      <Arrow x1={74} y1={220} x2={344} y2={220} label="k" markerId={markerId} color={accent} />
      <Arrow
        x1={60}
        y1={76}
        x2={60}
        y2={204}
        label="h"
        subLabel="Σ_h ⊣ h"
        markerId={markerId}
        color={accent}
      />
      <Arrow
        x1={360}
        y1={76}
        x2={360}
        y2={204}
        label="g"
        subLabel="Σ_g ⊣ g"
        markerId={markerId}
        color={accent}
      />
      {/* Mate equation — centered in the square, above the diagonal */}
      <text
        x={210}
        y={128}
        textAnchor="middle"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={700}
        fill={isHolds ? accent : failColor}
      >
        {isHolds ? "Σ_g ∘ f  ≅  k ∘ Σ_h" : "Σ_g ∘ f  ≄  k ∘ Σ_h"}
      </text>
      <text
        x={210}
        y={144}
        textAnchor="middle"
        fontSize="10"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        {isHolds ? "BC condition · free crossing" : "BC failure · seam"}
      </text>
      {/* Diagonal "free crossing" candidate. Solid (allowed morphism)
          when BC holds; dashed and stamped ✗ when BC fails. */}
      <Arrow
        x1={78}
        y1={72}
        x2={342}
        y2={208}
        label=""
        markerId={subtleMarkerId}
        color={isHolds ? accent : failColor}
        variant={isHolds ? "solid" : "dashed"}
        forbidden={!isHolds}
      />
      <text x={210} y={262} textAnchor="middle" fontSize="11" fill={GRAY[600]}>
        {isHolds
          ? "Crossing the square in either order yields naturally isomorphic results — data flows freely."
          : "Crossing the square in either order yields incompatible results — the free crossing is unconstructable."}
      </text>
    </svg>
  );
}

/**
 * SeamBridgeKanDiagram: shows, at a glance, how a Beck-Chevalley *failure*
 * becomes a CSK *seam*, and how a thick (witness-carrying) bridge is
 * exactly a restricted Kan extension datum across that seam.
 *
 *           BC fails                 governance
 *  T ∘ G ─────────▶  no free   ─────────────────▶  T ∘ G ⇒ G ∘ T
 *  (free λ)         distributive λ                 only via witness
 *
 * The picture is intentionally schematic — its job is to make the
 * structural slogan "seam = BC failure, bridge = restricted Kan datum"
 * legible without forcing the reader to parse a full fibration picture.
 */
export function SeamBridgeKanDiagram({
  accent = STROKE,
  ariaLabel = "BC failure becomes a CSK seam; the bridge is a witness-carrying restricted Kan extension datum",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const failColor = WARN_COLOR;
  return (
    <svg
      viewBox="0 0 560 220"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ArrowDefs id={`${markerId}-fail`} color={failColor} />
      <ObjectNode cx={70} cy={105} label="T∘G" accent={accent} />
      <ObjectNode cx={280} cy={105} label="G∘T" accent={accent} />
      <ObjectNode cx={490} cy={105} label="G∘T" accent={accent} />
      {/* Free distributive law λ — forbidden. */}
      <Arrow
        x1={90}
        y1={90}
        x2={260}
        y2={90}
        label="free λ"
        subLabel="forbidden"
        markerId={`${markerId}-fail`}
        color={failColor}
        variant="dashed"
        forbidden
        curve={32}
      />
      {/* Witness-carrying bridge. */}
      <Arrow
        x1={90}
        y1={120}
        x2={260}
        y2={120}
        label="bridge"
        subLabel="restricted Kan datum"
        markerId={markerId}
        color={accent}
        curve={-32}
      />
      <Arrow
        x1={298}
        y1={105}
        x2={472}
        y2={105}
        label="witness w"
        subLabel="consumed on use"
        markerId={markerId}
        color={accent}
      />
      <text
        x={175}
        y={192}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fontWeight={700}
        fill={failColor}
      >
        BC fails ⇒ seam
      </text>
      <text
        x={390}
        y={192}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fontWeight={700}
        fill={accent}
      >
        bridge = restricted Kan extension datum
      </text>
    </svg>
  );
}

/**
 * TwoAdjunctionDiagram: a 2-categorical adjunction, drawn as two
 * 1-functors `F`, `G` together with a 2-cell `α` that witnesses the
 * adjunction up to specified coherence (rather than on the nose).
 *
 *   C  ───── F ─────▶ D
 *   ◀═══════ G ═══════
 *       (α : 2-cell)
 *
 * The intent is to show that "higher" adjunctions replace equalities with
 * 2-cells whose composites must satisfy coherence; CSK uses this regime
 * for SDP-level adjunctions, where the seam is graded rather than binary.
 */
export function TwoAdjunctionDiagram({
  accent = STROKE,
  ariaLabel = "Two-adjunction: F ⊣ G with a 2-cell α witnessing the adjunction up to coherence",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 360 200"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={56} cy={100} label="C" accent={accent} />
      <ObjectNode cx={304} cy={100} label="D" accent={accent} />
      <Arrow
        x1={70}
        y1={86}
        x2={290}
        y2={86}
        label="F"
        markerId={markerId}
        color={accent}
        curve={30}
      />
      <Arrow
        x1={290}
        y1={114}
        x2={70}
        y2={114}
        label="G"
        markerId={markerId}
        color={accent}
        curve={-30}
      />
      {/* Double arrow indicating the 2-cell α between G ∘ F and Id_C. */}
      <g>
        <line
          x1={180}
          y1={50}
          x2={180}
          y2={150}
          stroke={accent}
          strokeWidth={2}
          strokeDasharray="6 3"
        />
        <line x1={172} y1={56} x2={188} y2={56} stroke={accent} strokeWidth={2} />
        <line x1={172} y1={144} x2={188} y2={144} stroke={accent} strokeWidth={2} />
        <text
          x={186}
          y={104}
          fontSize="14"
          fontFamily={DIAGRAM_MONO}
          fontWeight={700}
          fill={accent}
        >
          α
        </text>
      </g>
      <text
        x={180}
        y={180}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        α : G ∘ F ⇒ Id (up to coherence — a 2-cell, not an equality)
      </text>
    </svg>
  );
}

/**
 * GradedAdjunctionDiagram: an adjunction whose unit and counit are *graded*
 * — every leg is tagged by a grade drawn from a monoid. Crossings are
 * authorized only when the grade composition is derivable from the
 * grading monoid.
 *
 *   C  ── F_k ─▶ D
 *   ◀── G_k ───
 *
 *   η_k : Id ⇒ G_k ∘ F_k         ε_k : F_k ∘ G_k ⇒ Id_k
 */
export function GradedAdjunctionDiagram({
  accent = STROKE,
  grade = "k",
  ariaLabel,
}: {
  readonly accent?: string;
  readonly grade?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const label =
    ariaLabel ?? `Graded adjunction: F_${grade} ⊣ G_${grade} with grade-indexed unit and counit`;
  return (
    <svg
      viewBox="0 0 360 200"
      role="img"
      aria-label={label}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={56} cy={100} label="C" accent={accent} />
      <ObjectNode cx={304} cy={100} label="D" accent={accent} />
      <Arrow
        x1={70}
        y1={88}
        x2={290}
        y2={88}
        label={`F_${grade}`}
        markerId={markerId}
        color={accent}
        curve={28}
      />
      <Arrow
        x1={290}
        y1={112}
        x2={70}
        y2={112}
        label={`G_${grade}`}
        markerId={markerId}
        color={accent}
        curve={-28}
      />
      <text
        x={180}
        y={28}
        textAnchor="middle"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={600}
        fill={LABEL}
      >
        η
        <tspan baselineShift="sub" fontSize="9">
          {grade}
        </tspan>{" "}
        : Id ⇒ G
        <tspan baselineShift="sub" fontSize="9">
          {grade}
        </tspan>{" "}
        ∘ F
        <tspan baselineShift="sub" fontSize="9">
          {grade}
        </tspan>
      </text>
      <text
        x={180}
        y={178}
        textAnchor="middle"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={600}
        fill={LABEL}
      >
        ε
        <tspan baselineShift="sub" fontSize="9">
          {grade}
        </tspan>{" "}
        : F
        <tspan baselineShift="sub" fontSize="9">
          {grade}
        </tspan>{" "}
        ∘ G
        <tspan baselineShift="sub" fontSize="9">
          {grade}
        </tspan>{" "}
        ⇒ Id
      </text>
      <text x={180} y={138} textAnchor="middle" fontSize="14" fontWeight={700} fill={accent}>
        F
        <tspan baselineShift="sub" fontSize="10">
          {grade}
        </tspan>
        {" ⊣ "}G
        <tspan baselineShift="sub" fontSize="10">
          {grade}
        </tspan>
      </text>
    </svg>
  );
}
